import { DragDropModule } from '@angular/cdk/drag-drop';
import { LayoutModule } from '@angular/cdk/layout';
import { CommonModule, NgOptimizedImageModule } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule, ErrorHandler } from '@angular/core';
import { MatNativeDateModule } from '@angular/material/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import { AppRouterModule } from '@app/app-router.module';
import { HostModule } from '@app/host/host.module';
import { TableModule } from '@app/infrastructure/data-list/table/table.module';
import { HeadersInterceptor } from '@app/interceptors/headers.interceptor';
import { ShellAdminPanelModule } from '@app/shell-admin-panel/shell-admin-panel.module';
import { ShellPartnersModule } from '@app/shell-partners/shell-partners.module';
import { ShellModule } from '@app/shell/shell.module';
import { SidebarLayoutModule } from '@app/sidebar-layout/sidebar-layout.module';
import { AuthHttpInterceptor, AuthModule as Auth0Module } from '@auth0/auth0-angular';
import { SharedModule } from '@modules/shared/shared.module';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import * as Sentry from '@sentry/angular-ivy';
import { ModalDirective } from '@shared/directives/modal.directive';
import { PresenterModeModule } from '@shared/directives/presenter-mode/presenter-mode.module';
import { PipesModule } from '@shared/pipes/pipes.module';
import { SearchInputModule } from '@shared/search-input/search-input.module';
import { SpinnerModule } from '@shared/spinner/spinner.module';
import { ToasterModule, ToasterService } from 'angular2-toaster';
import { Ng2SearchPipeModule } from 'ng2-search-filter';
import { AlertConfig, AlertModule } from 'ngx-bootstrap/alert';
import { ButtonsModule } from 'ngx-bootstrap/buttons';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { ModalModule } from 'ngx-bootstrap/modal';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { NgxDaterangepickerMd } from 'ngx-daterangepicker-material';
import { PerfectScrollbarModule } from 'ngx-perfect-scrollbar';
import { AppComponent } from './app.component';
import { BillingApproveComponent } from './components/billing-approve/billing-approve.component';
import { P404Component } from './components/error/404.component';
import { ErrorScreenComponent } from './components/error/error-screen/error-screen.component';
import { ForbiddenComponent } from './components/forbidden/forbidden.component';
import { LoaderComponent } from './components/loader/loader.component';
import { HostModalComponent } from './components/modals/host-modal/host-modal.component';
import { ReservationGroupDrilldownComponent } from './components/reservation-group-drilldown/reservation-group-drilldown.component';
import { ReservationRowComponent } from './components/reservation-row/reservation-row.component';
import { SlackComponent } from './components/slack-integration/slack.component';
import { TeamsComponent } from './components/teams-integration/teams.component';
import { ButtonsModule as BtnsModule } from './infrastructure/buttons/buttons.module';
import { CacheService } from './services/cache.service';
import { SecurityService } from './services/security.service';
import { WrapToasterService } from './services/wrap-toaster-service';
import { NgxHotjarModule, NgxHotjarRouterModule } from 'ngx-hotjar';
import { environment } from '@environment';
import { SentryService } from './services/sentry.service';
import { MainLogoModule } from '@app/infrastructure/components/main-logo/main-logo.module';
import { CONFIG_INITIALIZER_PROVIDER } from '@app/infrastructure/auth/auth0-initializer';
import { ChangeUrlsInterceptor } from '@app/interceptors/change-urls-interceptor.service';
import { PotentialSavingModalModule } from './modules/potential-save-modal/potential-save-modal.module';

let importModules = [
	CommonModule,
	BtnsModule,
	HttpClientModule,
	BrowserModule,
	BrowserAnimationsModule,
	ToasterModule,
	BsDropdownModule.forRoot(),
	NgxDaterangepickerMd.forRoot(),
	MatNativeDateModule,
	TabsModule.forRoot(),
	CollapseModule.forRoot(),
	PerfectScrollbarModule,
	Ng2SearchPipeModule,
	Auth0Module.forRoot(),
	ModalModule.forRoot(),
	LayoutModule,
	ButtonsModule.forRoot(),
	DragDropModule,
	NgbDropdownModule,
	AlertModule,
	SharedModule,
	PipesModule,
	NgSelectModule,
	SpinnerModule,
	PresenterModeModule,
	HostModule,
	ShellModule,
	ShellPartnersModule,
	ShellAdminPanelModule,
	SidebarLayoutModule,
	AppRouterModule,
	TableModule,
	SearchInputModule,
	PotentialSavingModalModule,
];

if (environment.hotJar) {
	importModules.push(NgxHotjarModule.forRoot(environment.hotJar), NgxHotjarRouterModule);
}

@NgModule({
	declarations: [
		AppComponent,
		P404Component,
		ReservationRowComponent,
		ReservationGroupDrilldownComponent,
		SlackComponent,
		TeamsComponent,
		ErrorScreenComponent,
		LoaderComponent,
		ForbiddenComponent,
		ModalDirective,
		HostModalComponent,
		BillingApproveComponent,
	],
	imports: [importModules, NgOptimizedImageModule, MainLogoModule],
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
	providers: [
		{
			provide: ErrorHandler,
			useValue: Sentry.createErrorHandler(),
		},
		{
			provide: Sentry.TraceService,
			deps: [Router],
		},
		{
			useClass: AuthHttpInterceptor,
			provide: HTTP_INTERCEPTORS,
			multi: true,
		},
		{
			useClass: HeadersInterceptor,
			provide: HTTP_INTERCEPTORS,
			multi: true,
		},
		{
			useClass: ChangeUrlsInterceptor,
			provide: HTTP_INTERCEPTORS,
			multi: true,
		},
		SecurityService,
		AlertConfig,
		CacheService,
		ToasterService,
		WrapToasterService,
		CONFIG_INITIALIZER_PROVIDER,
		SentryService,
	],
	bootstrap: [AppComponent],
	exports: [ModalDirective, LoaderComponent],
})
export class AppModule {
	constructor(trace: Sentry.TraceService) {}
}
